import {
  Box,
  Button,
  Divider,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { getOperatorList, deleteOperators } from '../../api/menu';
import AnimationPage from '../../pages/AnimationPage';
import { errorMessages } from '../../utils/methods';
import CustomCheckbox from '../CustomCheckbox';
import { Link, useNavigate } from 'react-router-dom';
import BannerDeleteModal from '../banner/BannerDeleteModal';
import user from '../../reducers/user';

function SetUserList() {
  const { t } = useTranslation();
  const token = useSelector(function (state) {
    return state.user.user.token;
  });
  const initialQuery = {
    page: 1,
    limit: 10,
    searchInput: '',
  };

  const toast = useToast();
  const [list, setList] = useState([]);
  const [query, setQuery] = useState(initialQuery);
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const navigate = useNavigate();
  const getList = async () => {
    try {
      const data = await getOperatorList(query, token);
      setList(data);
    } catch (error) {
      console.error('API call failed:', error);
      toast.error(errorMessages(error));
    }
  };

  const handleChange = e => {
    const { name, checked } = e.target;
    var tempUser = {};
    if (name === 'allSelect') {
      tempUser = list?.content?.map(user => {
        return { ...user, isChecked: checked };
      });
    } else {
      tempUser = list?.content?.map(manager =>
        manager.username + manager.operatorId === name ? { ...manager, isChecked: checked } : manager,
      );
    }
    setList({ ...list, content: tempUser });
  };

  const handleChangePage = (_, value) => {
    setQuery({ ...query, page: value });
  };

  const deleteOperatorsByIds = async () => {
    const operatorIds = list?.content
      ?.filter(object => object.isChecked && object.operatorId) // Filter objects with isChecked=true and operatorId
      .map(object => `operatorIds=${object.operatorId}`) // Map to "operatorIds=value" format
      .join('&');
    try {
      await deleteOperators(operatorIds, token);
      toast.success(t('Deletion completed'));
      getList();
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  const getListBySearchInput = () => {
    setQuery({...query, searchInput : searchInput})
  }

  useEffect(() => {
    getList();
  }, [query]);

  return (
    <AnimationPage>
      <Box
        component={Paper}
        elavation={2}
        sx={{
          background: '#fff',
          borderRadius: '10px',
          padding: '30px 40px',
          width: '1080px',
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography className="title" sx={{ mr: '20px', fontSize: '12px', fontWeight: 500 }}>
              {t('Search')}
            </Typography>

            <TextField
              color="info"
              sx={{ width: '375px' }}
              size="small"
              name='searchInput'
              value={searchInput}
              onChange={e => {
                setSearchInput(e.target.value)
              }}
              placeholder={t('Search by name or ID')}
              id="outlined-search"
              type="search"
            />
            <Button
              sx={{
                ml: '4px',
                background: '#0F7F85',
                color: 'white',
                width: '62px',
                height: '36px',
                border: '1px solid #0F7F85',
                '&:hover': {
                  color: '#0F7F85',
                  background: '#fff',
                },
              }}
              onClick={getListBySearchInput}
              variant="text">
              {t('Search')}
            </Button>
          </Box>
          <Link to="/operator" style={{ textDecoration: 'none' }}>
            <Button
              sx={{
                width: '108px',
                height: '36px',
                borderRadius: '6px',
                pl: '21px',
                pr: '21px',
                fontSize: '13px',
                background: '#0F7F85',
                color: '#fff',
                '&:hover': { background: '#fff', color: '#0F7F85' },
              }}
              variant="outlined">
              {t('Operator registration')}
            </Button>
          </Link>
        </Box>
        <Divider sx={{ mb: '20px', mt: '26px', color: '#e1e1e1' }} />
        <TableContainer sx={{ border: '1px solid #ddd' }}>
          <Table sx={{ minWidth: 550 }} size="small" aria-label="a dense table">
            <TableHead sx={{ background: '#fafbfc' }}>
              <TableRow>
                <TableCell align="center" sx={{ borderRight: '1px solid #ddd' }}>
                  <CustomCheckbox
                    color="primary"
                    name="allSelect"
                    checked={!list.content?.some(user => user.isChecked !== true)}
                    onChange={handleChange}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: '1px solid #ddd',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                  align="center">
                  {t('Name')}
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: '1px solid #ddd',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                  align="center">
                  {t('ID')}
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: '1px solid #ddd',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                  align="center">
                  {t('Created date')}
                </TableCell>
                <TableCell
                  sx={{
                    borderRight: '1px solid #ddd',
                    fontSize: '12px',
                    fontWeight: 500,
                  }}
                  align="center">
                  {t('Last access date')}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 500 }}>
                  {t('State')}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {list?.content?.map((row, index) => (
                <TableRow
                  component={Link}
                  role="checkbox"
                  key={index}
                  sx={{
                    textDecoration: 'none',
                    // "&:hover": { background: "#ddd" },
                    '&:last-child td, &:last-child th': { borderBottom: 0 },
                  }}>
                  <TableCell align="center" padding="checkbox" sx={{ borderRight: '1px solid #ddd' }}>
                    <CustomCheckbox
                      name={row.username + row.operatorId}
                      checked={row?.isChecked || false}
                      onChange={handleChange}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderRight: '1px solid #ddd' }}
                    onClick={e => {
                      {
                        e.preventDefault();
                        navigate(`/operator/${row.operatorId}`);
                      }
                    }}
                    component="th"
                    scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderRight: '1px solid #ddd' }}
                    onClick={e => {
                      {
                        e.preventDefault();
                        navigate(`/operator/${row.operatorId}`);
                      }
                    }}>
                    {row.username}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderRight: '1px solid #ddd' }}
                    onClick={e => {
                      {
                        e.preventDefault();
                        navigate(`/operator/${row.operatorId}`);
                      }
                    }}>
                    {row.registeredDate}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderRight: '1px solid #ddd' }}
                    onClick={e => {
                      {
                        e.preventDefault();
                        navigate(`/operator/${row.operatorId}`);
                      }
                    }}>
                    {row.lastAccessDate}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderRight: '1px solid #ddd', color: row?.status === 'ACTIVE' ? '#0275FC' : '#222222' }}
                    onClick={e => {
                      {
                        e.preventDefault();
                        navigate(`/operator/${row.operatorId}`);
                      }
                    }}>
                    {t(row.status)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', mt: '10px', alignItems: 'flex-end' }}>
          <Button
            disabled={!list.content?.some(user => user.isChecked)}
            onClick={() => {
              setOpen(true);
            }}
            sx={{
              fontSize: '13px',
              background: 'transparent',
              color: '#fff',
              width: '85px',
              height: '36px',
              mr: '307px',
              fontWeight: '300',
              borderRadius: '8px',
              color: 'red',
              border: !list.content?.some(user => user.isChecked) ? '1px solid gray' : '1px solid #FF004F',
              '&:hover': { background: '#ef0c23', color: '#fff' },
            }}>
            {t('Delete selection')}
          </Button>
          <Stack spacing={1}>
            <Pagination
              count={Math.ceil(list?.totalElements / query.limit) || 0}
              onChange={handleChangePage}
              page={query.page}
              color="primary"
              variant="outlined"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium': {
                  background: '#0F7F85',
                  border: 'none',
                  height: '24px',
                  minWidth: '24px',
                },
                '& .MuiPaginationItem-root.MuiPaginationItem-sizeMedium.Mui-selected': {
                  background: '#0F7F85',
                  color: '#fff',
                },
                '.MuiPaginationItem-icon': {
                  color: '#fff',
                },
              }}
            />
          </Stack>
        </Box>
        <BannerDeleteModal open={open} setOpen={setOpen} handleDelete={deleteOperatorsByIds} />
      </Box>
    </AnimationPage>
  );
}

export default SetUserList;
